import { useCallback } from 'react';

import { api } from '@shared/api';
import { bigifyLoan, Loan } from '@shared/api/@types/loans';
import env from '@shared/config';
import { TimesEnum } from '@shared/enums';
import { UserStore } from '@shared/store';
import { Uuid, UuidPrefix } from '@shared/types/uuid';

import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import Big from 'big.js';
import { DateTime } from 'luxon';

const MOCK = Boolean(env.MOCK_LOANS);

const useFetchLoans = (
  options?: Omit<UseQueryOptions<Loan[], unknown, Loan[], (string | undefined)[]>, 'queryKey' | 'queryFn'>,
) => {
  const { getUniqueIdentifier } = UserStore.useUserStore;

  const fetchLoans = useCallback(async () => {
    if (MOCK) {
      return new Promise<Loan[]>((resolve) =>
        setTimeout(
          () =>
            resolve([
              {
                loanId: 'loan_2x4y6z8A1b3c5d7E9fGhJkLmN' as Uuid<UuidPrefix.Loan>,
                borrowAssetCode: 'AUD',
                borrowPrincipalAmount: new Big('5000.00'),
                createdAt: DateTime.fromMillis(1720673391000),
                repaymentType: 'LINE_OF_CREDIT',
                securityAmount: new Big('0.15'),
                securityAssetCode: 'BTC',
                status: 'OPEN',
                termInDays: new Big(12),
                interestRateBps: Big(1),
                termValue: 1,
                userId: 'usr_AESfouF9gcWutZd2G2EtuP' as Uuid<UuidPrefix.User>,
                validUntil: DateTime.fromMillis(1752209391000),
                securityCryptoCurrency: 'BTC',
                securityCryptoAmount: new Big(0.00072883),
                fiatCurrency: 'AUD',
                securityFiatAmount: new Big(131.04),
                pendingWithdrawSecurityCryptoAmount: new Big(0.0002),
                loanToValueStatus: 'HEALTHY',
                loanToValue: new Big(0.3903),
                loanToValue24hTrend: new Big(0.0102),
                borrowedFiatPrincipalAmount: new Big(0),
                pendingBorrowFiatPrincipalAmount: new Big(0),
                fiatFeeRatio: null,
                fiatPrincipalAvailableToBorrow: null,
                fiatLoanLimit: null,
                maxBorrowLtv: null,
                principalRepaid: null,
                principalLeft: new Big(200),
                defaultNoticeLimit: null,
                repaymentNoticeLimit: null,
                attentionLimit: null,
                maxLimit: null,
              } as Loan,
            ]),
          1000,
        ),
      );
    }

    const response = await api.endpoints.getLoans();
    return response.data.map(bigifyLoan);
  }, []);

  return useQuery<Loan[], unknown, Loan[], (string | undefined)[]>({
    queryKey: ['loans', getUniqueIdentifier()],
    queryFn: fetchLoans,
    keepPreviousData: true,
    staleTime: TimesEnum.MINUTE * 5,
    ...options,
  });
};

export { useFetchLoans };
